import { useEffect, useLayoutEffect, useState } from "react";
import { useNotification } from "../../components/notification/NotificationContainer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  addProjectAccount,
  getAccountCategories,
  getAccountTypes,
  unsetAccountCategories,
  unsetSelectedAccount,
} from "../../features/accountsSlice";
import { getProject, unsetSelectedProject } from "../../features/projectsSlice";
import Loading from "../../components/loading/Loading";
import MainContainer from "../../components/container/MainContainer";
import PageTitle from "../../components/titles/PageTitle";
import InputField from "../../components/form/InputField";
import SelectField from "../../components/form/SelectField";
import { zodResolver } from "@hookform/resolvers/zod";
import { addProjectAccountSchema } from "../../schemas";
import loadStatus from "../../util/loadStatus";
import NetworkError from "../error/NetworkError";

const CreateProjectAccount = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const project = useSelector((state) => state.projects.selected);
  const projectLoad = useSelector((state) => state.projects.load.getProject);
  const accountTypes = useSelector((state) => state.accounts.accountTypes);

  const addAccountLoad = useSelector(
    (state) => state.accounts.load.addProjectAccount
  );

  const accountCategories = useSelector(
    (state) => state.accounts.accountCategories
  );
  const accountTypesLoad = useSelector(
    (state) => state.accounts.load.getAccountTypes
  );
  const accountCategoriesLoad = useSelector(
    (state) => state.accounts.load.getAccountCategories
  );
  const accountLoad = useSelector((state) => state.accounts.load.getAccount);
  let [categories, setCategories] = useState(accountCategories);
  const addNotification = useNotification();
  const {
    register,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { project_id: project.id },
    resolver: zodResolver(addProjectAccountSchema),
  });

  const accountType = watch("account_type_id");

  const onSubmit = async (data, e) => {
    e.preventDefault();
    try {
      const res = await dispatch(addProjectAccount(data));
      if (addProjectAccount.fulfilled.match(res)) {
        addNotification("Successfully saved!", "success");
        return navigate("/projects/accounts");
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.message, "warning");
    }
  };

  useLayoutEffect(() => {
    dispatch(getProject(id));

    if (accountTypes.length <= 0) {
      dispatch(getAccountTypes());
    }
    if (accountCategories.length <= 0) {
      dispatch(getAccountCategories());
    }

    return () => {
      dispatch(unsetAccountCategories());
      dispatch(unsetSelectedAccount());
      unsetSelectedProject();
    };
  }, []);

  useEffect(() => {
    if (!accountType) {
      setCategories([]);
    } else {
      setCategories(
        accountCategories.filter((val) => val.account_type_id === accountType)
      );
    }
  }, [accountType]);

  useEffect(() => {
    if (project.id) {
      reset({ project_id: project.id, name: project.name });
    }
  }, [project]);

  if (
    accountCategoriesLoad === loadStatus.loading ||
    accountTypesLoad === loadStatus.loading ||
    accountLoad === loadStatus.loading ||
    projectLoad === loadStatus.loading
  ) {
    return <Loading />;
  }

  if (
    accountCategoriesLoad === loadStatus.error ||
    accountTypesLoad === loadStatus.error ||
    accountLoad === loadStatus.error ||
    projectLoad === loadStatus.error
  ) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <div className="w-full max-w-5xl mx-auto">
        <PageTitle title="Project Account" />
        {project && (
          <form
            onSubmit={(e) => handleSubmit(onSubmit)(e)}
            method="post"
            encType="multipart/form-data"
          >
            <div className="text-text-1/70  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 font-roboto">
              <h5 className="text-text-1/80 ">Create Project Account</h5>
              <div className="p-4">
                <p className="flex gap-3">
                  Project Title: <span>{project.name}</span>
                </p>
              </div>
            </div>
            <div className="text-text-1/60  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 bg-content sm:px-20 lg:px-36">
              <InputField
                register={register}
                name="name"
                autoComplete="off"
                errors={errors.name}
                label="Project Account Name"
                type="text"
              />
              <div>
                <SelectField
                  name="account_type_id"
                  label="Account Type"
                  register={register}
                  data={[
                    { id: "", name: "-- Select Account Type" },
                    ...accountTypes,
                  ]}
                  errors={errors.account_type_id}
                />
                <SelectField
                  name="category_id"
                  label="Category"
                  register={register}
                  data={[{ id: "", name: "-- Select Category" }, ...categories]}
                  errors={errors.category_id}
                />
                <InputField
                  register={register}
                  name="project_id"
                  autoComplete="off"
                  errors={errors.project_id}
                  type="hidden"
                  defaultValue={project.project_id}
                />
              </div>
              <div className="flex justify-between items-center pt-4 pb-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    // dispatch(unsetSelectedProject());
                    navigate("/projects/accounts");
                  }}
                  className="cancel-rss-btn"
                  disabled={addAccountLoad === loadStatus.loading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={addAccountLoad === loadStatus.loading}
                  className="add-rss-btn"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </MainContainer>
  );
};

export default CreateProjectAccount;
